import * as bootstrap from "bootstrap"
import { navigator as turboNavigator } from "@hotwired/turbo"

// Modals, the URL, and the history buttons.
//
// Keep the URL in step with the open modal, the way the goal-progress modal
// already does for scoring: opening writes ?modal_url=<the modal's URL>,
// closing takes it back out, and loading a URL that carries it re-opens that
// modal over the page underneath. So a fullscreen modal is linkable ("here's
// the focus area I mean"), and back/forward have something real to move
// between — opening a modal pushes an entry for the *same page*, so back is
// no longer "leave the plan view", it is "close the modal", and forward
// re-opens it. No page render either way: the page underneath never changed.
//
// Deliberately not the documents subapp's pop_modal_url, which looks
// identical but means something else: the server puts that one on a form
// action or a redirect to say "after this navigation, pop that modal", and
// it is meant to outlive a save — that is how saving inside a folder modal
// returns you to the folder. modal_url is only ever written here, names the
// modal that is open right now, and dies with it.
//
// ponytail: a query param rather than routing each modal to its own path.
// The param already works end to end, and it says "the plan view, with this
// modal on top" — which a modal's own URL cannot, since those render a
// standalone form page when they are not fetched into the frame.

const PARAM = "modal_url"

const remoteModalFrame = () => document.getElementById("remote_modal")

const currentUrl = () => new URL(window.location.href)

const modalUrlInAddressBar = () => currentUrl().searchParams.get(PARAM)

const topOpenModal = () => [...document.querySelectorAll(".modal.show")].pop()

const urlWith = (modalUrl) => {
  const url = currentUrl()
  url.searchParams.set(PARAM, modalUrl)
  return url
}

const urlWithout = () => {
  const url = currentUrl()
  url.searchParams.delete(PARAM)
  return url
}

// Two URLs are the same page when only the open modal differs.
const samePage = (a, b) => {
  const [x, y] = [new URL(a), new URL(b)]
  x.searchParams.delete(PARAM)
  y.searchParams.delete(PARAM)

  return x.href === y.href
}

// Turbo's history, not the browser's: the entry carries Turbo's restoration
// state, so a later back or forward off it is still a real Turbo navigation.
const pushUrl = (url) => {
  turboNavigator.history.push(url)
  pageUrl = window.location.href
}

const replaceUrl = (url) => {
  turboNavigator.history.replace(url)
  pageUrl = window.location.href
}

// Clicking a link is how pop_modal_controller has always done it: it goes
// through Turbo's frame machinery, so the modal loads and shows exactly as
// it does from a button.
const popModal = (modalUrl) => {
  const link = document.createElement("a")
  link.href = modalUrl
  link.dataset.turboFrame = "remote_modal"
  link.dataset.turbo = "true"
  document.body.appendChild(link)
  link.click()
  link.remove()
}

// The page the modal is sitting on, so a pop can be told from a page change.
let pageUrl = window.location.href

// Writing the URL

document.addEventListener("shown.bs.modal", (event) => {
  const frame = remoteModalFrame()
  if (!frame?.src || !frame.contains(event.target)) { return }
  // Already there when the modal was popped from the URL in the first place.
  if (modalUrlInAddressBar() === frame.src) { return }

  pushUrl(urlWith(frame.src))
})

document.addEventListener("hidden.bs.modal", () => {
  if (!modalUrlInAddressBar()) { return }
  // An outer modal in a stack is still up and still owns the URL.
  if (topOpenModal()) { return }

  // replace, not push: closing a modal should not cost a back press.
  replaceUrl(urlWithout())
})

// A save inside a modal answers with turbo_stream.refresh, which Turbo
// resolves against document.baseURI — leave the param in place and the
// refresh re-opens the modal that just saved. submit fires before the
// response lands, which is early enough.
document.addEventListener("submit", (event) => {
  if (!modalUrlInAddressBar()) { return }
  if (!event.target.closest(".modal")) { return }

  replaceUrl(urlWithout())
})

// Reading the URL

document.addEventListener("turbo:load", () => {
  pageUrl = window.location.href

  const modalUrl = modalUrlInAddressBar()
  if (modalUrl && !topOpenModal()) { popModal(modalUrl) }
})

window.addEventListener("popstate", () => {
  const previous = pageUrl
  pageUrl = window.location.href

  if (samePage(previous, pageUrl)) {
    // Same page, different modal. Turbo has already started a restoration
    // visit (it hears popstate first) that would re-render a page identical
    // to the one on screen — a wasted plan-view render and a lost scroll
    // position. Stop it and just match the modal to the URL.
    turboNavigator.currentVisit?.cancel()
    syncModalToUrl()
    return
  }

  // A genuine page change. Modals that write the URL never reach here — back
  // off one lands on the same page — but modals opened without one (local
  // data-bs-toggle modals) do, and letting the visit run would discard their
  // contents with no dirty-form prompt: restoration visits skip
  // turbo:before-visit. Swallow the pop, put the page back, close the modal.
  const modal = topOpenModal()
  if (!modal) { return }

  turboNavigator.currentVisit?.cancel()
  pushUrl(new URL(previous))
  bootstrap.Modal.getInstance(modal)?.hide()
})

function syncModalToUrl() {
  const wanted = modalUrlInAddressBar()
  const modal = topOpenModal()

  if (modal && wanted) { return }

  if (modal) {
    bootstrap.Modal.getInstance(modal)?.hide()
    // Still up: the dirty-form confirm was declined. The modal won, so give
    // it its URL back rather than leaving the two disagreeing.
    if (modal.classList.contains("show")) { pushUrl(urlWith(remoteModalFrame()?.src)) }
  } else if (wanted) {
    popModal(wanted)
  }
}

// A closed remote modal leaves its markup behind in the frame. Baked into a
// page snapshot, restoring that snapshot re-connects remote_modal_controller
// — which show()s on connect — and the modal pops open on a page whose URL
// says no modal is open. Empty the frame before it is cached. A modal that
// is still open stays: it is in the URL, so restoring it is right.
document.addEventListener("turbo:before-cache", () => {
  const frame = remoteModalFrame()
  if (!frame || frame.querySelector(".modal.show")) { return }

  frame.replaceChildren()
})
